/**
 * The dates the "Next dates" block lists — pure, synchronous, the TS twin of
 * the DB rule (`@releve/shared/recurring/schedule`). Split from the component
 * so `RecurringNextDatesPreview.tsx` exports only a component (fast refresh)
 * and the math is testable without rendering.
 */
import { addDays } from 'date-fns';
import {
  firstOccurrenceOnOrAfter,
  previewOccurrenceDates,
  type RecurringDocumentType,
  type RecurringFrequency,
  type RecurringTemplateBaseWire,
} from '@releve/shared';
import { formatLocalISODate, parseLocalISODate } from '@/lib/dateUtils';

/** How many upcoming dates the block lists. */
export const RECURRING_PREVIEW_COUNT = 5;

export interface RecurringNextDatesPreviewProps {
  documentType: RecurringDocumentType;
  frequency: RecurringFrequency;
  interval_count: number;
  /** `YYYY-MM-DD` — may be mid-edit / invalid; the preview then lists nothing. */
  start_date: string;
  /** Resolved end bounds (undefined / null = none). */
  end_date?: string | null;
  max_occurrences?: number | null;
  /** AP/AR — appended to the schedule words. */
  due_days?: number | null;
  /** {@link todayInOrgZone} — "today" for the first-date sentence and the edit-mode base. */
  orgToday: string;
  /** Template-edit mode: the saved row (its cursor + generated count bound the list). */
  template?: Pick<
    RecurringTemplateBaseWire,
    'start_date' | 'last_occurrence_date' | 'occurrences_generated' | 'status'
  > | null;
}

/**
 * The dates the block lists, or `null` when the inputs cannot form a schedule
 * yet (an invalid or half-typed start). Exported for the section's tests.
 */
export function computePreviewDates(props: RecurringNextDatesPreviewProps): string[] | null {
  const { frequency, interval_count, start_date, end_date, max_occurrences, orgToday, template } = props;
  if (!Number.isInteger(interval_count) || interval_count < 1) return null;
  const schedule = { frequency, interval_count, start_date };
  try {
    let from: string | undefined;
    if (template) {
      // PATCH recompute base: GREATEST(org-today, last generated + 1).
      const lastDate = template.last_occurrence_date ? parseLocalISODate(template.last_occurrence_date) : null;
      const lastPlusOne = lastDate ? formatLocalISODate(addDays(lastDate, 1)) : null;
      const base = lastPlusOne && lastPlusOne > orgToday ? lastPlusOne : orgToday;
      const first = firstOccurrenceOnOrAfter(schedule, start_date, base);
      if (first == null) return [];
      from = first;
    }
    return previewOccurrenceDates(schedule, RECURRING_PREVIEW_COUNT, {
      from,
      end_date: end_date ?? null,
      max_occurrences: max_occurrences ?? null,
      occurrences_generated: template?.occurrences_generated ?? 0,
    });
  } catch {
    // RangeError from an unparseable start — the schema reports it inline.
    return null;
  }
}

